<template>
    <div>
        <!-- Show an error alert if there is a 422 or any other error -->
        <v-alert
            v-if="errorMessage"
            type="error"
            dismissible
            class="mb-4"
            @input="clearError"
        >
            {{ errorMessage }}
        </v-alert>

        <v-form
            v-model="valid"
            ref="form"
            @submit.prevent="onSubmit"
        >
            <v-row>
                <!-- Event -->
                <v-col cols="12" sm="6">
                    <v-select
                        v-model="form.event"
                        :items="eventOptions"
                        item-text="text"
                        item-value="value"
                        label="Event"
                        :rules="[rules.required]"
                        required
                    />
                </v-col>

                <!-- Routine Num -->
                <v-col cols="12" sm="6">
                    <v-select
                        v-model="form.routine_num"
                        :items="routineNumOptions"
                        item-text="text"
                        item-value="value"
                        label="Routine"
                        :rules="[rules.required]"
                        required
                    />
                </v-col>
            </v-row>

            <v-row>
                <!-- Score Component -->
                <v-col cols="12" sm="6">
                    <v-select
                        v-model="form.component"
                        :items="filteredComponentOptions"
                        item-text="text"
                        item-value="value"
                        label="Score Component"
                        :rules="[rules.required]"
                        required
                    />
                </v-col>

                <!-- Value -->
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="form.value"
                        label="Value"
                        type="number"
                        :rules="[rules.required, rules.numeric]"
                        required
                    />
                </v-col>
            </v-row>

            <v-row>
                <!-- Private -->
                <v-col cols="12" sm="6">
                    <v-switch
                        v-model="form.is_private"
                        label="Private Goal"
                        color="primary"
                        hint="Hide this goal from the public"
                        persistent-hint
                    />
                </v-col>

                <!-- Auto Deactivate -->
                <v-col cols="12" sm="6">
                    <v-switch
                        v-model="form.auto_deactivate"
                        label="Auto Deactivate?"
                        color="primary"
                        hint="Deactivate this goal automatically once it has been achieved or acknowledge it every time"
                        persistent-hint
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" class="text-right">
                    <!-- Disable the button if the form is not valid or we're saving -->
                    <v-btn
                        color="primary"
                        :disabled="!valid || saving"
                        :loading="saving"
                        @click="onSubmit"
                    >
                        Save
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
    </div>
</template>

<script>
export default {
    name: 'AthleteGoalForm',

    props: {
        saving: {
            type: Boolean,
            default: false,
        },

        /**
         * The full error object/string from the parent.
         * This might be a 422 from axios (which typically
         * includes error.response.data.errors).
         */
        error: {
            type: [String, Object],
            default: null,
        },
    },

    data() {
        return {
            valid: false, // For v-form validation
            form: {
                event: 'TI',            // 'TI', 'DM', 'TU', 'TS'
                routine_num: 'any',    // '1', '2', 'any', 'all' (or numeric if you prefer)
                component: '',        // e.g. 'single_execution_score', etc.
                value: null,
                is_private: false,
                auto_deactivate: true,
            },
            eventOptions: [
                { text: 'Trampoline (TI)',  value: 'TI' },
                { text: 'Double Mini (DM)', value: 'DM' },
                { text: 'Tumbling (TU)',    value: 'TU' },
                { text: 'Synchro (TS)',     value: 'TS' },
            ],
            routineNumOptions: [
                { text: 'Any',             value: 'any' },
                { text: 'First Routine',   value: '1' },
                { text: 'Second Routine',  value: '2' },
                { text: 'All',             value: 'all' },
            ],
            componentOptions: [
                { text: 'Single Execution Score',  value: 'single_execution_score' },
                { text: 'Total Execution Score',   value: 'total_execution_score' },
                { text: 'Difficulty',              value: 'difficulty' },
                { text: 'Horizontal Displacement', value: 'horizontal_displacement' },
                { text: 'Time of Flight',          value: 'time_of_flight' },
                { text: 'Synchronization Score',   value: 'sync' },
                { text: 'Neutral Deduction',       value: 'neutral_deduction' },
                { text: 'Routine Score',           value: 'routine_score' },
                { text: 'Total Score',             value: 'total_score' },
            ],
            rules: {
                required: value => !!value || 'Required.',
                numeric: value => !isNaN(parseFloat(value)) || 'Must be a number',
            },
        }
    },

    computed: {
        /**
         * Returns a string containing the error message(s) for display.
         * If your 422 includes an "errors" object with multiple fields,
         * you can flatten them here.
         */
        errorMessage() {
            if (!this.error) {
                return ''
            }

            // If it's an Axios error object, try to parse the typical 422 structure:
            if (this.error.response && this.error.response.status === 422) {
                const errors = this.error.response.data?.errors
                if (errors && typeof errors === 'object') {
                    // Flatten all field errors into a single string or multiple lines
                    return Object.values(errors)
                        .flat() // in case each field has multiple messages
                        .join(' ')
                }
                // If there's a single message or a different structure:
                return this.error.response.data?.message || 'Validation error occurred.'
            }

            // If it's not a 422 or doesn't have that structure, just show a fallback
            if (typeof this.error === 'string') {
                return this.error
            }
            return 'An error occurred.'
        },

        filteredComponentOptions() {
            let options = [...this.componentOptions]

            // Filter by event
            switch (this.form.event) {
                case 'TI':
                    // Exclude 'sync'
                    options = options.filter(opt => opt.value !== 'sync')
                    break
                case 'DM':
                case 'TU':
                    // Exclude 'horizontal_displacement', 'time_of_flight', 'sync'
                    options = options.filter(opt =>
                        !['horizontal_displacement', 'time_of_flight', 'sync'].includes(opt.value)
                    )
                    break
                case 'TS':
                    // Exclude 'time_of_flight'
                    options = options.filter(opt => opt.value !== 'time_of_flight')
                    // If we want to rename 'sync' to 'synchronization_score',
                    // we could add or update it here, but let's skip that
                    // since we already have { text: 'Synchronization Score', value: 'sync' }
                    break
            }

            // Filter out 'total_score' if routine_num is 1 or 2
            // (NOTE: these are strings because of your data, so parse them accordingly)
            const routineNumInt = parseInt(this.form.routine_num, 10)
            if ([1, 2].includes(routineNumInt)) {
                options = options.filter(opt => opt.value !== 'total_score')
            }

            return options
        },
    },

    methods: {
        onSubmit() {
            if (this.$refs.form.validate()) {
                // Emit form data
                this.$emit('save', { ...this.form })
            }
        },

        clearError() {
            // This is triggered when the user dismisses the alert
            this.$emit('error-cleared')
        },
    },
}
</script>
